import React, { useState } from "react";
// Bewusst der ROHE react-router-Link, NICHT utils/localizedNav: der Wrapper würde
// das Ziel-Präfix anhand der aktuellen (unpräfixierten) URL wieder abstreifen.
import { Link, useLocation } from "react-router-dom";
import useIsMobile from "../hooks/useIsMobile";
import {
  ALL_LANGS,
  splitLangPrefix,
  buildLocalizedPath,
  LANG_OVERRIDE_KEY,
  detectBrowserLang,
} from "../utils/langRouting";

const DISMISS_KEY = "aof-lang-suggest-dismissed";

// Kurztexte in der jeweiligen ZIEL-Sprache — ein deutschsprachiger Besucher auf
// einer englischen URL soll den Hinweis auf Deutsch lesen. Nur 9 Sprachen × 2
// kurze Strings: bewusst als lokale Konstante statt als neue i18n-Keys in 10
// translation.json-Dateien (die t()-Funktion liefert hier ohnehin die Sprache
// der aktuellen URL, nicht die des Vorschlags).
const SUGGEST = {
  de: { label: "Deutsch",    text: "Diese Seite ist auch auf Deutsch verfügbar.",          cta: "Auf Deutsch ansehen" },
  it: { label: "Italiano",   text: "Questa pagina è disponibile anche in italiano.",       cta: "Vedi in italiano" },
  ja: { label: "日本語",      text: "このページは日本語でもご覧いただけます。",                cta: "日本語で見る" },
  es: { label: "Español",    text: "Esta página también está disponible en español.",      cta: "Ver en español" },
  pt: { label: "Português",  text: "Esta página também está disponível em português.",     cta: "Ver em português" },
  fr: { label: "Français",   text: "Cette page est aussi disponible en français.",         cta: "Voir en français" },
  ru: { label: "Русский",    text: "Эта страница также доступна на русском языке.",         cta: "Смотреть на русском" },
  pl: { label: "Polski",     text: "Ta strona jest również dostępna po polsku.",           cta: "Zobacz po polsku" },
  tr: { label: "Türkçe",     text: "Bu sayfa Türkçe olarak da mevcuttur.",                 cta: "Türkçe görüntüle" },
};

export default function LangSuggestionBanner() {
  const isMobile = useIsMobile();
  const { pathname } = useLocation();
  const [dismissed, setDismissed] = useState(() => {
    try {
      return window.localStorage.getItem(DISMISS_KEY) === "1";
    } catch {
      return false;
    }
  });

  const { lang: urlLang, basePath } = splitLangPrefix(pathname);

  // Nur auf der unpräfixierten (englischen) URL vorschlagen — auf /de/… ist der
  // Nutzer bereits in einer lokalisierten Variante.
  if (urlLang !== "en" || dismissed) return null;

  let preferred = null;
  try {
    const override = window.localStorage.getItem(LANG_OVERRIDE_KEY);
    preferred = override && ALL_LANGS.includes(override) ? override : detectBrowserLang();
  } catch {
    preferred = detectBrowserLang();
  }

  if (!preferred || preferred === "en" || !SUGGEST[preferred]) return null;

  const s = SUGGEST[preferred];
  const target = buildLocalizedPath(preferred, basePath);

  const dismiss = () => {
    try {
      window.localStorage.setItem(DISMISS_KEY, "1");
    } catch {}
    setDismissed(true);
  };

  // Klick = bewusste Wahl: LANG_OVERRIDE_KEY setzen, damit der Vorschlag nicht
  // wiederkehrt und der Header-Schalter konsistent bleibt.
  const choose = () => {
    try {
      window.localStorage.setItem(LANG_OVERRIDE_KEY, preferred);
    } catch {}
    dismiss();
  };

  return (
    <div
      style={{
        maxWidth: 760,
        margin: isMobile ? "8px 12px" : "14px auto",
        background: "linear-gradient(135deg, #eef6ff, #dcecff)",
        border: "1.5px solid #8fbff0",
        borderRadius: 16,
        padding: isMobile ? "12px 14px" : "13px 20px",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        flexWrap: "wrap",
        gap: 10,
        boxShadow: "0 3px 16px rgba(80,140,220,0.18)",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 10, flex: 1, minWidth: 0 }}>
        <span style={{ fontSize: 22, flexShrink: 0 }}>🌐</span>
        <div style={{ fontWeight: 700, fontSize: isMobile ? 13 : 14, color: "#1a4a80", lineHeight: 1.35 }}>
          {s.text}
        </div>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
        <Link
          to={target}
          onClick={choose}
          hrefLang={preferred}
          style={{
            background: "linear-gradient(135deg, #3d8bfd, #2570e0)",
            color: "#fff",
            borderRadius: 999,
            padding: isMobile ? "7px 14px" : "8px 18px",
            fontWeight: 800,
            fontSize: isMobile ? 12 : 13,
            textDecoration: "none",
            boxShadow: "0 2px 8px rgba(53,120,224,0.35)",
            whiteSpace: "nowrap",
          }}
        >
          {s.cta}
        </Link>
        <button
          type="button"
          onClick={dismiss}
          aria-label="Dismiss"
          style={{
            border: "none",
            background: "none",
            cursor: "pointer",
            color: "#5a8bc0",
            fontSize: 18,
            lineHeight: 1,
            padding: "2px 4px",
          }}
        >
          ✕
        </button>
      </div>
    </div>
  );
}
